/**
 * A descriptor card whose printed boxes are stored on `ics_incident_info`.
 *
 * Several FEMA forms print narrative boxes that all live on the one incident
 * info record: the 202's command emphasis, the 205's special instructions, the
 * 206's emergency procedures. The descriptor names those boxes after the form
 * (`specialInstructions`), while the record names them after the incident
 * (`communicationsSpecialInstructions`), so each card supplies the mapping.
 *
 * Boxes owned by an existing table or editor come in through `slots` and keep
 * their own saves; only the mapped sources are committed from here.
 *
 * Each box saves on its own, but every save writes the whole record:
 * `handleSave` receives server state with one box laid over it, so the columns
 * the user did not touch are rewritten with what they already held.
 */

import type React from 'react';
import { useCallback, useMemo, type ReactNode } from 'react';
import { useIncidentInfo, useUpdateIcsInfo } from '../hooks/incidentInfoHooks';
import {
  useIncidentPermissions,
  useScopedIncident,
} from '../hooks/incidentHooks';
import { useRecordFormVersionSave } from '../hooks/icsFormVersionHooks';
import { useOperationalPeriodWindow } from '../hooks/useOperationalPeriodWindow';
import { useICSContext } from '../planningView/ICSContext';

import { DescriptorCard, type DescriptorCardValues } from './DescriptorCard';
import { DescriptorSectionField } from './DescriptorSectionField';
import FormVersioningBanner from './FormVersioningBanner';
import {
  booleanIncidentInfoSources,
  incidentInfoSources,
  isVersionedForm,
  pratusSection,
} from './formSectionSchema';

/** A Pratus field with no printed box: `[incidentInfoKey, label]`. */
export type ExtraField = readonly [string, string];

export type FemaIncidentInfoCardProps = {
  baseId: string;
  extraFields?: readonly ExtraField[];
  slots?: Record<string, ReactNode>;
  omit?: string[];
  banner?: ReactNode;
  headerActions?: ReactNode;
  /** Editors with their own save and their own validation. */
  legacyExtras?: ReactNode;
};

export const FemaIncidentInfoCard: React.FC<FemaIncidentInfoCardProps> = ({
  baseId,
  extraFields = [],
  slots,
  omit,
  banner,
  headerActions,
  legacyExtras,
}) => {
  const incident = useScopedIncident();
  const { data: incidentInfo } = useIncidentInfo();
  const updateIcsInfo = useUpdateIcsInfo();
  const { data: permissions } = useIncidentPermissions();
  const disabled = !(permissions?.['incident:editCards'] ?? false);
  const { effectivePeriodNumber, readOnlyForms } = useICSContext();
  const recordVersion = useRecordFormVersionSave(baseId, effectivePeriodNumber);
  const { range: operationalPeriod } = useOperationalPeriodWindow();

  const fieldMap = useMemo(() => incidentInfoSources(baseId), [baseId]);
  const booleans = useMemo(
    () => new Set(booleanIncidentInfoSources(baseId)),
    [baseId]
  );

  const values = useMemo<DescriptorCardValues>(() => {
    const info = (incidentInfo ?? {}) as Record<string, unknown>;
    const picked: DescriptorCardValues = {};
    for (const source of Object.keys(fieldMap)) {
      picked[source] =
        info[fieldMap[source]] ?? (booleans.has(source) ? false : '');
    }
    for (const [key] of extraFields) {
      picked[key] = info[key] ?? '';
    }
    return picked;
  }, [incidentInfo, fieldMap, booleans, extraFields]);

  const handleSave = useCallback(
    (submitted: DescriptorCardValues) => {
      const next: Record<string, unknown> = { ...incidentInfo };
      for (const source of Object.keys(fieldMap)) {
        next[fieldMap[source]] = submitted[source];
      }
      for (const [key] of extraFields) {
        next[key] = submitted[key];
      }
      updateIcsInfo.mutate(next as Parameters<typeof updateIcsInfo.mutate>[0], {
        onSuccess: () => {
          // The version snapshots what the server now holds, so it can only run
          // once the write above has landed.
          if (isVersionedForm(baseId) && !readOnlyForms) {
            recordVersion.mutate();
          }
        },
      });
    },
    [
      incidentInfo,
      fieldMap,
      extraFields,
      updateIcsInfo,
      baseId,
      readOnlyForms,
      recordVersion,
    ]
  );

  const extras =
    extraFields.length || legacyExtras ? (
      <>
        {extraFields.map(([key, label]) => (
          <DescriptorSectionField
            key={key}
            section={pratusSection(key, label)}
            formBaseId={baseId}
            disabled={disabled}
          />
        ))}
        {legacyExtras}
      </>
    ) : undefined;

  return (
    <DescriptorCard
      baseId={baseId}
      family="fema"
      values={values}
      onSave={handleSave}
      disabled={disabled}
      banner={
        <FormVersioningBanner baseId={baseId}>{banner}</FormVersioningBanner>
      }
      headerActions={headerActions}
      autoValues={{ incidentName: incident?.name, operationalPeriod }}
      slots={slots}
      omit={omit}
      extras={extras}
      extrasSources={extraFields.map(([key]) => key)}
    />
  );
};

export default FemaIncidentInfoCard;
